/**
 * The settings schema and its defaults. Settings.ts loads, saves and exports
 * the settings themselves; the balance data is in GameData.ts and the shared
 * constants in Constants.ts.
 */
import type { ControlScheme, KeyBindsConfig, SOCDMode } from './types/Input';

/**
 * Renderer preference for Pixi.js
 * - 'webgpu': Prefer WebGPU, automatically falling back to WebGL where
 *   unsupported (default — autoDetectRenderer handles the fallback)
 * - 'webgl': Force WebGL
 *
 * A legacy 'auto' value existed historically; it passed no preference, and
 * Pixi's default preference is WebGL — making it indistinguishable from
 * 'webgl'. Stored 'auto' values are read as the default at the read sites.
 */
export type RendererPreference = 'webgpu' | 'webgl';

/**
 * Sprite theme preference
 * - 'default': Standard aircraft sprites
 * - 'hitshapes': Shows server collision shapes instead of aircraft
 */
export type Theme = 'default' | 'hitshapes';

/**
 * The display settings the menu's Graphics group offers, each with a value:
 * the saved one, or its DISPLAY_DEFAULTS entry. Device-specific, so they stay
 * on this device rather than syncing to the account, as GPU capabilities
 * vary. DisplaySettings is the saved shape. Adding one here: its default
 * below, its control in menu-drawer.ts, and an effect in GameAPI's display
 * group when the game has to act on a change.
 */
export interface DisplaySettingValues {
  /** Preferred renderer type */
  rendererPreference: RendererPreference;
  /** Sprite theme */
  theme: Theme;
  /** Show map boundary outlines in hitshapes theme */
  outlines: boolean;
  /** Show background grid in hitshapes theme */
  grid: boolean;
  /** Apply terrain tint to mountains in hitshapes theme */
  mountainTint: boolean;
  /** Show subtle fill on map polygons in hitshapes theme */
  mapFill: boolean;
  /** Render at the screen's pixel ratio: sharp on phones and HiDPI displays, at four to nine times the fill, which the reference phone holds. Read at boot, so it takes effect on the next load. */
  hidpi: boolean;
}

/**
 * Display settings as saved (local-only, not synced): the settings the player
 * has changed, and the sizes the game remembers for them.
 */
export interface DisplaySettings extends Partial<DisplaySettingValues> {
  /** Minimap scale multiplier (0.75 to 3.0) */
  minimapScale?: number;
  /** Saved chat panel width in pixels */
  chatWidth?: number;
  /** Saved chat panel height in pixels */
  chatHeight?: number;
}

/**
 * What each display setting is until the player changes it. Only changed
 * settings are saved, so a new default here reaches everyone who hasn't
 * touched that setting. Read a setting as
 * `settings.display?.X ?? DISPLAY_DEFAULTS.X`, never with a literal.
 *
 * The type requires an entry for every display setting. The retired 'auto'
 * renderer value reads as the WebGPU-first default: GameAPI's display group
 * and setupPixiRenderer each look for an explicit 'webgl'. minimapScale's
 * default is MINIMAP_SCALE_DEFAULT, and the saved chat size has none.
 */
export const DISPLAY_DEFAULTS = {
  rendererPreference: 'webgpu',
  theme: 'default',
  outlines: true,
  grid: true,
  mountainTint: true,
  mapFill: true,
  hidpi: true,
} as const satisfies DisplaySettingValues;

/**
 * The game settings the menu offers, each with a value: the saved one, or its
 * GAME_DEFAULTS entry. The account syncs them across devices. GameSettings is
 * the saved shape. Adding one here: its default below, its control in
 * menu-drawer.ts, and an effect in GameAPI's game group when the game has to
 * act on a change.
 */
export interface GameSettingValues {
  /** Hide bots from leaderboard and scoreboard, and bot-only game log lines */
  ignoreBots: boolean;
  /** Minimap bot styling: smaller dots in all modes, additionally greyed in FFA
   *  (CTF keeps team colors). Top-3 crowned players are exempt. */
  botsColor: boolean;
  /** Show laser pointer line indicating ship direction */
  laserPointer: boolean;
  /** Show a warning banner when an enemy Prowler you can't see is within 600u,
   *  whatever you fly. StarMash limited its radar to Predator and Tornado. */
  prowlerAlert: boolean;
  /** Voice commentary on CTF flag taken/captured/returned events */
  voiceEventsCtf: boolean;
  /** Show CTF spawn-boundary lines on the map (blue/red verticals + half-width horizontals) */
  ctfSpawnLines: boolean;
  /** Keep the camera centered on the player even at map edges (extends viewport past map bounds) */
  extendMapEdges: boolean;
  /** Cap the game viewport width at 1.15× its height and center the canvas
   *  (StarMash's "Preserve an almost-squared game area"). DOM UI stays
   *  full-window. Desktop-only. */
  squaredScene: boolean;
  /** Show rank and teammate health on nameplates */
  extendedNameplates: boolean;
  /** Show leaderboard rank prefix "3." on nameplates (only effective when extendedNameplates is on) */
  nameplateRank: boolean;
  /** Show teammate health bracket "[85]" on nameplates (only effective when extendedNameplates is on) */
  teammateHealth: boolean;
  /** Scale nameplate text size with the current zoom level so names stay readable when zoomed out */
  scaleNameplates: boolean;
  /** Show game log panel with kill feed */
  gameLog: boolean;
  /** Show kills in game log */
  gameLogKills: boolean;
  /** Show player join/leave in game log */
  gameLogJoinLeave: boolean;
  /** Show CTF events in game log */
  gameLogCtf: boolean;
  /** Show BTR events in game log */
  gameLogBtr: boolean;
  /** Show command hints above chat input */
  showCommandHints: boolean;
  /** SOCD resolution mode for opposing key inputs */
  socdMode: SOCDMode;
  /** Touch control scheme (mobile only): point-to-aim (default) or directional */
  controlScheme: ControlScheme;
  /** Precise aim for touch point-to-aim (dead-reckoned turn pulses that land on target — see the
   *  precise-aim block in Input.ts; off = original pulse steering) */
  preciseAim: boolean;
}

/** Game settings as saved (synced across devices): the settings the player has changed */
export type GameSettings = Partial<GameSettingValues>;

/**
 * What each game setting is until the player changes it, with the same rules
 * as DISPLAY_DEFAULTS. Read a setting as
 * `settings.game?.X ?? GAME_DEFAULTS.X`, never with a literal or a
 * bare `=== false` or truthy test, which hide the default.
 *
 * The type requires an entry for every game setting. socdMode and
 * controlScheme name a mode, and a saved value outside the current ones (the
 * removed 'neutral' SOCD mode) reads as the default: GameAPI's game group
 * lists the modes, and Input.ts's readers look for the one non-default mode.
 */
export const GAME_DEFAULTS = {
  ignoreBots: false,
  botsColor: true,
  laserPointer: false,
  prowlerAlert: false,
  voiceEventsCtf: true,
  ctfSpawnLines: false,
  extendMapEdges: true,
  squaredScene: false,
  extendedNameplates: true,
  nameplateRank: true,
  teammateHealth: true,
  scaleNameplates: false,
  gameLog: true,
  gameLogKills: true,
  gameLogJoinLeave: true,
  gameLogCtf: true,
  gameLogBtr: true,
  showCommandHints: true,
  socdMode: 'off',
  controlScheme: 'pointToAim',
  preciseAim: false,
} as const satisfies GameSettingValues;

export interface Settings {
  /** Unique player identifier (local-only, not synced) */
  id?: string;
  /** Display settings (local-only, not synced) */
  display?: DisplaySettings;
  /** Game settings (synced across devices) */
  game?: GameSettings;
  /** Whether mobile welcome message has been shown */
  mobileshown?: boolean;
  /** Player's preferred name */
  name?: string;
  /** Preferred game region */
  region?: string;
  /** Player's country flag code */
  flag?: string;
  /** Sound effects enabled */
  sound?: boolean;
  /** Mouse control mode enabled */
  mousemode?: boolean;
  /** Whether help screen has been shown to the user */
  helpshown?: boolean;
  /** Custom key bindings configuration */
  keybinds?: KeyBindsConfig;
  /** Whether alternate scaling mode is enabled */
  scalingAltMode?: boolean;
  /** Scaling factor value for normal mode */
  scalingFactor?: number;
  /** Scaling factor value for alternate mode */
  altScalingFactor?: number;
  /**
   * StarMash's radio messages, as it syncs them into the account; the radio
   * adopts them when this client has no save of its own. Shape checked on read.
   */
  SWAM_RadioMessages?: unknown;
}
